import React from 'react';
import type { CSSProperties } from 'react';
import { format_talk_time } from './outcome';
import type { CodeLabel } from './outcome';
import { LABEL_CLASS } from './ui';

interface Props {
  outcomes: CodeLabel[];
  outcome: string;
  on_outcome: (code: string) => void;
  require_outcome: boolean;
  talk_time_seconds: number;
  override_minutes: string;
  on_override: (minutes: string) => void;
  disabled?: boolean;
  error?: string;
  // One row (desktop footer) instead of a stacked block.
  inline?: boolean;
}

// Mirrors goserver's maxTalkTimeOverrideSeconds.
const MAX_OVERRIDE_MINUTES = 240;

const pill_style = (on: boolean): CSSProperties => ({
  border: `1px solid ${on ? 'var(--brand-primary)' : 'var(--border-subtle)'}`,
  background: on ? 'var(--brand-primary-soft)' : 'var(--surface-card)',
  color: on ? 'var(--text-link-brand)' : 'var(--text-secondary)',
  fontWeight: on ? 600 : 400
});

// Outcome as pill radios: a real (visually hidden) radio per pill keeps the
// labels, keyboard arrows and the radiogroup semantics of the old list.
function OutcomePanel({
  outcomes,
  outcome,
  on_outcome,
  require_outcome,
  talk_time_seconds,
  override_minutes,
  on_override,
  disabled = false,
  error = '',
  inline = false
}: Props) {
  return (
    <div
      data-testid="outcome-panel"
      className={inline ? 'flex flex-wrap items-center gap-x-4 gap-y-1.5' : 'flex flex-col gap-2'}
    >
      <fieldset className="m-0 border-0 p-0">
        {/* Floated, the legend sits on the pills' line in the one-row footer layout. */}
        <legend
          className={`p-0 ${inline ? 'float-left mb-0 mr-2.5 leading-[30px]' : 'mb-2'} ${LABEL_CLASS}`}
        >
          Outcome
          {require_outcome ? (
            <span className="font-medium normal-case tracking-normal"> (required)</span>
          ) : null}
        </legend>
        <div role="radiogroup" aria-label="Outcome" className="flex flex-wrap gap-1.5">
          {outcomes.map((o) => {
            const on = outcome === o.code;
            return (
              <label
                key={o.code}
                htmlFor={`sales-task-outcome-${o.code}`}
                className={`relative inline-flex ${disabled ? 'cursor-default opacity-60' : 'cursor-pointer'}`}
              >
                <input
                  id={`sales-task-outcome-${o.code}`}
                  type="radio"
                  name="sales-task-outcome"
                  value={o.code}
                  checked={on}
                  disabled={disabled}
                  onChange={() => on_outcome(o.code)}
                  className="peer sr-only !h-px"
                />
                <span
                  className="inline-flex h-[30px] items-center whitespace-nowrap rounded-full px-3 text-xs peer-focus-visible:ring-2 peer-focus-visible:ring-brand-primary max-sm:h-9"
                  style={pill_style(on)}
                >
                  {o.label}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
      {talk_time_seconds > 0 ? (
        <div className="text-xs text-content-muted">
          Talk time{' '}
          <span className="font-semibold text-content-primary">
            {format_talk_time(talk_time_seconds)}
          </span>
          , from calls since you claimed
        </div>
      ) : (
        <label
          htmlFor="sales-task-talk-override"
          className="inline-flex items-center gap-2 text-xs text-content-muted"
          title="No calls were logged since you claimed. Enter minutes spent if you talked outside the dialer."
        >
          Talk time (min)
          <input
            id="sales-task-talk-override"
            type="number"
            min={0}
            max={MAX_OVERRIDE_MINUTES}
            step={1}
            value={override_minutes}
            disabled={disabled}
            onChange={(e) => on_override(e.target.value)}
            className="rounded-[7px] text-[12.5px] !h-[30px] max-sm:!h-11"
            // The CMS's global input rule forces 46px with !important.
            style={{
              width: 64,
              padding: '0 8px',
              margin: 0,
              border: '1px solid var(--input-border)',
              background: 'var(--input-bg)',
              color: 'var(--input-text)'
            }}
          />
        </label>
      )}
      {error ? (
        <div role="alert" className="text-xs" style={{ color: 'var(--state-error-text)' }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}

export default OutcomePanel;
